/**
 * Type definitions for Device Management
 */

import i18n from '@/i18n/config';

export type DeviceStatus = 'STOCK' | 'CUSTOMER' | 'VEHICLE' | 'LOST' | 'DECOMMISSIONED';

export interface Device {
  id: number;
  imei: string;
  status: DeviceStatus;
  iccid?: string;
  sim_status?: string;
  last_known_vin?: string;
  vehicle_id?: number;
  organization_id?: string;
  vehicle_registration?: string;
  vehicle_brand?: string;
  vehicle_model?: string;
  organization_name?: string;
  created_at: string;
  updated_at: string;
}

export interface DeviceHistory {
  id: number;
  change_type: string;
  change_source: string;
  previous_value?: Record<string, any>;
  new_value?: Record<string, any>;
  performed_by_name?: string;
  notes?: string;
  created_at: string;
}

export interface DeviceStats {
  total: number;
  byStatus: Record<DeviceStatus, number>;
}

export interface StatusConfig {
  label: string;
  labelEn: string;
  color: string;
  bgColor: string;
}

function deviceLabel(key: string): string {
  return i18n.t(key);
}

export const statusConfig: Record<DeviceStatus, StatusConfig> = {
  STOCK: {
    get label() { return deviceLabel('copy.hooks.deviceStatusStock'); },
    get labelEn() { return deviceLabel('copy.hooks.deviceStatusStock'); },
    color: 'text-blue-600',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
  },
  CUSTOMER: {
    get label() { return deviceLabel('copy.hooks.deviceStatusCustomer'); },
    get labelEn() { return deviceLabel('copy.hooks.deviceStatusCustomer'); },
    color: 'text-purple-600',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
  },
  VEHICLE: {
    get label() { return deviceLabel('copy.hooks.deviceStatusVehicle'); },
    get labelEn() { return deviceLabel('copy.hooks.deviceStatusVehicle'); },
    color: 'text-green-600',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
  },
  LOST: {
    get label() { return deviceLabel('copy.hooks.deviceStatusLost'); },
    get labelEn() { return deviceLabel('copy.hooks.deviceStatusLost'); },
    color: 'text-orange-600',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
  },
  DECOMMISSIONED: {
    get label() { return deviceLabel('copy.hooks.deviceStatusDecommissioned'); },
    get labelEn() { return deviceLabel('copy.hooks.deviceStatusDecommissioned'); },
    color: 'text-textSecondary',
    bgColor: 'bg-gray-100 dark:bg-gray-700',
  },
};

export const changeTypeLabels: Record<string, { sv: string; en: string }> = {
  STATUS_CHANGE: {
    get sv() { return deviceLabel('copy.hooks.deviceChangeStatus'); },
    get en() { return deviceLabel('copy.hooks.deviceChangeStatus'); },
  },
  VEHICLE_MOUNTED: {
    get sv() { return deviceLabel('copy.hooks.deviceChangeMounted'); },
    get en() { return deviceLabel('copy.hooks.deviceChangeMounted'); },
  },
  VEHICLE_UNMOUNTED: {
    get sv() { return deviceLabel('copy.hooks.deviceChangeUnmounted'); },
    get en() { return deviceLabel('copy.hooks.deviceChangeUnmounted'); },
  },
  VEHICLE_TRANSFERRED: {
    get sv() { return deviceLabel('copy.hooks.deviceChangeTransferred'); },
    get en() { return deviceLabel('copy.hooks.deviceChangeTransferred'); },
  },
  VIN_DETECTED: {
    get sv() { return deviceLabel('copy.hooks.deviceChangeVin'); },
    get en() { return deviceLabel('copy.hooks.deviceChangeVin'); },
  },
  VEHICLE_CREATED: {
    get sv() { return deviceLabel('copy.hooks.deviceChangeVehicleCreated'); },
    get en() { return deviceLabel('copy.hooks.deviceChangeVehicleCreated'); },
  },
  SIM_ACTIVATED: {
    get sv() { return deviceLabel('copy.hooks.deviceChangeSimActivated'); },
    get en() { return deviceLabel('copy.hooks.deviceChangeSimActivated'); },
  },
  SIM_SUSPENDED: {
    get sv() { return deviceLabel('copy.hooks.deviceChangeSimSuspended'); },
    get en() { return deviceLabel('copy.hooks.deviceChangeSimSuspended'); },
  },
  DEVICE_CREATED: {
    get sv() { return deviceLabel('copy.hooks.deviceChangeCreated'); },
    get en() { return deviceLabel('copy.hooks.deviceChangeCreated'); },
  },
  DEVICE_IMPORTED: {
    get sv() { return deviceLabel('copy.hooks.deviceChangeImported'); },
    get en() { return deviceLabel('copy.hooks.deviceChangeImported'); },
  },
};
